// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
  MAX_FILE_SIZE: parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 10485760 // 10MB
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  USER: 'USER',
  CUSTOMER: 'CUSTOMER',
  SUPPLIER: 'SUPPLIER'
};

// Ticket Status
export const TICKET_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  ON_HOLD: 'ON_HOLD',
  COMPLETED: 'COMPLETED',
  REOPENED: 'REOPENED',
  RECLOSED: 'RECLOSED',
  CANCELLED: 'CANCELLED',
  ESCALATED: 'ESCALATED'
};

// Ticket Priority
export const TICKET_PRIORITY = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL'
};

// Status Labels
export const STATUS_LABELS = {
  [TICKET_STATUS.NOT_STARTED]: 'Not Started',
  [TICKET_STATUS.IN_PROGRESS]: 'In Progress',
  [TICKET_STATUS.ON_HOLD]: 'On Hold',
  [TICKET_STATUS.COMPLETED]: 'Completed',
  [TICKET_STATUS.REOPENED]: 'Reopened',
  [TICKET_STATUS.RECLOSED]: 'Reclosed',
  [TICKET_STATUS.CANCELLED]: 'Cancelled',
  [TICKET_STATUS.ESCALATED]: 'Escalated'
};

// Priority Labels
export const PRIORITY_LABELS = {
  [TICKET_PRIORITY.LOW]: 'Low',
  [TICKET_PRIORITY.MEDIUM]: 'Medium',
  [TICKET_PRIORITY.HIGH]: 'High',
  [TICKET_PRIORITY.CRITICAL]: 'Critical'
};

// Role Labels
export const ROLE_LABELS = {
  [USER_ROLES.ADMIN]: 'Administrator',
  [USER_ROLES.USER]: 'User',
  [USER_ROLES.CUSTOMER]: 'Customer',
  [USER_ROLES.SUPPLIER]: 'Supplier'
};

// Notification Types
export const NOTIFICATION_TYPES = {
  EMAIL: 'EMAIL',
  SMS: 'SMS',
  IN_APP: 'IN_APP',
  WHATSAPP: 'WHATSAPP'
};

// File Types
export const ALLOWED_FILE_TYPES = {
  IMAGES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  DOCUMENTS: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
  ],
  ALL: [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
  ]
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: parseInt(import.meta.env.VITE_DEFAULT_PAGE_SIZE) || 20,
  MAX_PAGE_SIZE: parseInt(import.meta.env.VITE_MAX_PAGE_SIZE) || 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100]
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'DD/MM/YYYY',
  DISPLAY_WITH_TIME: 'DD/MM/YYYY HH:mm',
  ISO: 'YYYY-MM-DD',
  ISO_WITH_TIME: 'YYYY-MM-DDTHH:mm:ss'
};

// Indian States
export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
];

// Theme Colors
export const THEME_COLORS = {
  PRIMARY: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a'
  },
  SUCCESS: {
    50: '#ecfdf5',
    100: '#d1fae5',
    500: '#10b981',
    600: '#059669'
  },
  WARNING: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706'
  },
  ERROR: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626'
  }
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'rdx_auth_token',
  REFRESH_TOKEN: 'rdx_refresh_token',
  USER_DATA: 'rdx_user',
  THEME: 'rdx_theme',
  SIDEBAR_COLLAPSED: 'rdx_sidebar_collapsed',
  NOTIFICATION_PREFERENCES: 'rdx_notification_preferences'
};

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  TICKETS: '/tickets',
  CREATE_TICKET: '/tickets/create',
  TICKET_DETAILS: '/tickets/:id',
  USERS: '/users',
  SETTINGS: '/settings',
  PROFILE: '/profile',
  NOT_FOUND: '*'
};

// Permissions
export const PERMISSIONS = {
  CREATE: 'can_create',
  VIEW: 'can_view',
  EDIT: 'can_edit',
  DELETE: 'can_delete',
  ASSIGN: 'can_assign',
  CLOSE: 'can_close'
};

// Default Modules
export const DEFAULT_MODULES = [
  { name: 'Finance', description: 'Financial operations and billing' },
  { name: 'Operations', description: 'Day-to-day operational activities' },
  { name: 'Customer Service', description: 'Customer support and relations' },
  { name: 'Human Resources', description: 'Employee related matters' },
  { name: 'IT Support', description: 'Technical issues and system support' },
  { name: 'Fleet Management', description: 'Vehicle and transportation' },
  { name: 'Warehouse', description: 'Storage and inventory management' },
  { name: 'Compliance', description: 'Legal and regulatory compliance' }
];

// SLA Default Times (in hours)
export const SLA_DEFAULTS = {
  [TICKET_PRIORITY.CRITICAL]: { response: 1, resolution: 4, escalation: 2 },
  [TICKET_PRIORITY.HIGH]: { response: 2, resolution: 24, escalation: 4 },
  [TICKET_PRIORITY.MEDIUM]: { response: 4, resolution: 48, escalation: 8 },
  [TICKET_PRIORITY.LOW]: { response: 8, resolution: 72, escalation: 16 }
};

// Chart Colors
export const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // yellow
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
  '#ec4899', // pink
  '#6b7280'  // gray
];

// Regex Patterns
export const REGEX_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[1-9]\d{1,14}$/,
  PINCODE: /^\d{6}$/,
  VEHICLE_NUMBER: /^[A-Z]{2}[-\s]?[0-9]{1,2}[-\s]?[A-Z]{1,3}[-\s]?[0-9]{1,4}$/,
  SHIPMENT_NUMBER: /^SHP-\d{4}-\d{3,6}$/,
  ORDER_NUMBER: /^ORD-\d{4}-\d{4,6}$/,
  TICKET_NUMBER: /^RDX-\d{4}-\d{6}$/
};

// Error Messages
export const ERROR_MESSAGES = {
  REQUIRED_FIELD: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_PHONE: 'Please enter a valid phone number',
  INVALID_PINCODE: 'Pincode must be 6 digits',
  PASSWORD_TOO_SHORT: 'Password must be at least 8 characters',
  PASSWORDS_DONT_MATCH: 'Passwords do not match',
  FILE_TOO_LARGE: 'File size exceeds the maximum limit',
  INVALID_FILE_TYPE: 'File type is not supported',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action',
  SESSION_EXPIRED: 'Your session has expired. Please login again.',
  SOMETHING_WENT_WRONG: 'Something went wrong. Please try again.'
};

// Success Messages
export const SUCCESS_MESSAGES = {
  TICKET_CREATED: 'Ticket created successfully',
  TICKET_UPDATED: 'Ticket updated successfully',
  TICKET_DELETED: 'Ticket deleted successfully',
  USER_CREATED: 'User created successfully',
  USER_UPDATED: 'User updated successfully',
  USER_DELETED: 'User deleted successfully',
  SETTINGS_SAVED: 'Settings saved successfully',
  FILE_UPLOADED: 'File uploaded successfully',
  PASSWORD_CHANGED: 'Password changed successfully',
  PROFILE_UPDATED: 'Profile updated successfully'
};

// Feature Flags
export const FEATURES = {
  ENABLE_NOTIFICATIONS: import.meta.env.VITE_ENABLE_NOTIFICATIONS === 'true',
  ENABLE_REAL_TIME_UPDATES: import.meta.env.VITE_ENABLE_REAL_TIME_UPDATES === 'true',
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  ENABLE_DARK_MODE: import.meta.env.VITE_ENABLE_DARK_MODE === 'true',
  DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === 'true'
};

// Animation Durations
export const ANIMATION = {
  FAST: 0.15,
  NORMAL: 0.3,
  SLOW: 0.5,
  VERY_SLOW: 1.0
};

// Breakpoints
export const BREAKPOINTS = {
  XS: '475px',
  SM: '640px',
  MD: '768px',
  LG: '1024px',
  XL: '1280px',
  '2XL': '1536px'
};

// Z-Index Scale
export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1010,
  FIXED: 1020,
  MODAL_BACKDROP: 1030,
  MODAL: 1040,
  POPOVER: 1050,
  TOOLTIP: 1060,
  NOTIFICATION: 1070
};

export default {
  API_CONFIG,
  USER_ROLES,
  TICKET_STATUS,
  TICKET_PRIORITY,
  STATUS_LABELS,
  PRIORITY_LABELS,
  ROLE_LABELS,
  NOTIFICATION_TYPES,
  ALLOWED_FILE_TYPES,
  PAGINATION,
  DATE_FORMATS,
  INDIAN_STATES,
  THEME_COLORS,
  STORAGE_KEYS,
  ROUTES,
  PERMISSIONS,
  DEFAULT_MODULES,
  SLA_DEFAULTS,
  CHART_COLORS,
  REGEX_PATTERNS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  FEATURES,
  ANIMATION,
  BREAKPOINTS,
  Z_INDEX
};